export const MERCARI_CONSTANTS = {
	DEBOUNCE_DELAY: 300,
	PRICE_LIMITS: {
		MIN: 300,
		MAX: 9999999,
	},
	DEFAULT_VALUES: {
		CATEGORY: 'all',
		SORT: 'recommended',
		ALL_CATEGORY_LABEL: '全部商品',
	},
	CONDITION_MAP: {
		'new-unused': '1',
		'almost-new': '2',
		'no-scratches': '3',
		'some-scratches': '4',
		scratches: '5',
		'bad-condition': '6',
	},
	MARKETPLACE_MAP: {
		individual: '1',
		shops: '2',
	},
	SORT_CONFIG: {
		price_low: { sort: 'price', order: 'asc' },
		price_high: { sort: 'price', order: 'desc' },
		newest: { sort: 'created_time', order: 'desc' },
		recommended: { sort: 'recommended' },
	},
} as const

export type ConditionKey = keyof typeof MERCARI_CONSTANTS.CONDITION_MAP
export type MarketplaceKey = keyof typeof MERCARI_CONSTANTS.MARKETPLACE_MAP
export type SortKey = keyof typeof MERCARI_CONSTANTS.SORT_CONFIG

export function isValidPrice(priceStr: string): boolean {
	if (!priceStr || priceStr === '') return true
	const price = parseInt(priceStr, 10)
	return (
		price >= MERCARI_CONSTANTS.PRICE_LIMITS.MIN &&
		price <= MERCARI_CONSTANTS.PRICE_LIMITS.MAX
	)
}

export function isValidPriceRange(minStr: string, maxStr: string): boolean {
	if (!minStr || !maxStr) return true
	const min = parseInt(minStr, 10)
	const max = parseInt(maxStr, 10)
	return min <= max
}

export function processPriceInput(rawValue: string): string {
	let numericValue = rawValue.replace(/[^\d]/g, '')

	if (numericValue.length > 1 && numericValue.startsWith('0')) {
		numericValue = numericValue.replace(/^0+/, '') || '0'
	}

	return numericValue
}

export function validatePriceState(priceState: {
	min: string
	max: string
}): boolean {
	const isMinValid = isValidPrice(priceState.min)
	const isMaxValid = isValidPrice(priceState.max)
	const isRangeValid = isValidPriceRange(priceState.min, priceState.max)

	return isMinValid && isMaxValid && isRangeValid
}

export interface FilterOption {
	id: string
	label: string
	value: string
}

export interface FilterSection {
	title: string
	options: FilterOption[]
}

export interface FilterConfig {
	category?: FilterSection
	itemCondition?: FilterSection
	status?: FilterSection
	marketplace?: FilterSection
	sort?: FilterSection
}

export const defaultFilterConfig: FilterConfig = {
	category: {
		title: '分類',
		options: [{ id: 'all', label: '全部商品', value: 'all' }],
	},
	itemCondition: {
		title: '商品狀態',
		options: [
			{ id: 'new-unused', label: '全新/未使用', value: 'new-unused' },
			{ id: 'almost-new', label: '幾乎全新', value: 'almost-new' },
			{ id: 'no-scratches', label: '無明顯劃痕或痕跡', value: 'no-scratches' },
			{ id: 'some-scratches', label: '有些劃痕/痕跡', value: 'some-scratches' },
			{ id: 'scratches', label: '有劃痕/痕跡', value: 'scratches' },
			{ id: 'bad-condition', label: '狀況不佳', value: 'bad-condition' },
		],
	},
	status: {
		title: '供應狀態',
		options: [
			{ id: 'on_sale', label: '販售中', value: 'on_sale' },
			{ id: 'sold_out', label: '售完', value: 'sold_out' },
		],
	},
	marketplace: {
		title: '賣家類型',
		options: [
			{ id: 'individual', label: '個人', value: 'individual' },
			{ id: 'shops', label: '商店', value: 'shops' },
		],
	},
	sort: {
		title: '排序方式',
		options: [
			{ id: 'recommended', label: '推薦', value: 'recommended' },
			{ id: 'newest', label: '最新', value: 'newest' },
			{ id: 'price_low', label: '價格由低至高', value: 'price_low' },
			{ id: 'price_high', label: '價格由高至高', value: 'price_high' },
		],
	},
}

export function getFilterConfig(
	customConfig?: Partial<FilterConfig>,
): FilterConfig {
	if (!customConfig) return defaultFilterConfig

	return {
		category: customConfig.category || defaultFilterConfig.category,
		itemCondition:
			customConfig.itemCondition || defaultFilterConfig.itemCondition,
		status: customConfig.status || defaultFilterConfig.status,
		marketplace: customConfig.marketplace || defaultFilterConfig.marketplace,
		sort: customConfig.sort || defaultFilterConfig.sort,
	}
}

// ============ Google Analytics ============

export function getSortingMethodLabel(sortValue: string): string {
	const sortingLabels: Record<string, string> = {
		recommended: 'Recommended',
		newest: 'Newest',
		price_low: 'Price Low to High',
		price_high: 'Price High to Low',
	}

	return sortingLabels[sortValue] || 'Recommended'
}

export function getCategoryLevels(filterState: any): Record<string, string> {
	if (!filterState || !filterState.categoryLevels) {
		return {
			item_category: '',
			item_category2: '',
			item_category3: '',
			item_category4: '',
		}
	}

	const result: Record<string, string> = {
		item_category: '',
		item_category2: '',
		item_category3: '',
		item_category4: '',
	}

	let { categoryLevels } = filterState

	if (
		categoryLevels &&
		typeof categoryLevels === 'object' &&
		'value' in categoryLevels
	) {
		categoryLevels = categoryLevels.value
	} else if (!Array.isArray(categoryLevels)) {
		categoryLevels = []
	}

	for (let i = 0; i < categoryLevels.length && i < 4; i += 1) {
		const level = categoryLevels[i]
		if (level && level.selectedId && level.selectedId !== 'all') {
			const selectedOption = level.options?.find(
				(opt: any) => opt.value === level.selectedId,
			)
			if (selectedOption) {
				const fieldName = i === 0 ? 'item_category' : `item_category${i + 1}`
				result[fieldName] = selectedOption.label || ''
			}
		}
	}

	return result
}

export function getFilterTrackingValues(
	filterState: any,
): Record<string, string> {
	const result: Record<string, string> = {}

	if (!filterState) return result

	if (filterState.itemCondition && filterState.itemCondition.length > 0) {
		result.filter_condition = filterState.itemCondition
			.map(
				(id: string) =>
					MERCARI_CONSTANTS.CONDITION_MAP[
						id as keyof typeof MERCARI_CONSTANTS.CONDITION_MAP
					] || id,
			)
			.join('|')
	}

	if (filterState.status && filterState.status.length > 0) {
		result.filter_availability = filterState.status.join('|')
	}

	if (
		(filterState.price?.min && filterState.price.min !== '') ||
		(filterState.price?.max && filterState.price.max !== '')
	) {
		const minPrice =
			filterState.price?.min || MERCARI_CONSTANTS.PRICE_LIMITS.MIN.toString()
		const maxPrice =
			filterState.price?.max || MERCARI_CONSTANTS.PRICE_LIMITS.MAX.toString()
		result.filter_price = `${minPrice}-${maxPrice}`
	}

	if (filterState.marketplace && filterState.marketplace.length > 0) {
		const marketplaceValues = filterState.marketplace.map(
			(value: string) =>
				MERCARI_CONSTANTS.MARKETPLACE_MAP[
					value as keyof typeof MERCARI_CONSTANTS.MARKETPLACE_MAP
				] || value,
		)
		result.filter_lister = marketplaceValues.join('|')
	}

	return result
}
